import React, { useState, useCallback, useEffect } from "react";
import { CONTRACT_TYPES } from "../constants/contractTypes";
import LaborContractPreview from "../templates/LaborContractPreview";
import CollaboratorContractPreview from "../templates/CollaboratorContractPreview";
import ToolBarCustom from "src/components/ToolBarCustom";
import { css } from "../style/styles";
import { calculateEndDate, formatDateInput } from "../utils/formatDate";
import { getPlaceTitle } from "../utils/formatPlace";
import { Select, DatePicker, Alert, message } from "antd";
import dayjs from "dayjs";
import "../style/selectStyle.css";

function validateField(field, value) {
    const isEmpty =
        value === undefined ||
        value === null ||
        (typeof value === "string" && value.trim() === "") ||
        (field.type === "checkbox" && !value);

    if (field.required && isEmpty) {
        return `${field.label} là bắt buộc`;
    }

    if (!isEmpty) {
        if (field.key === "salary") {
            const raw = String(value).replace(/,/g, "");
            if (isNaN(Number(raw)) || Number(raw) < 0) {
                return "Mức lương phải là số không âm";
            }
        }

        if (field.type === "date") {
            const d = new Date(value);
            if (isNaN(d.getTime())) return "Ngày không hợp lệ";
        }

        if (field.key === "phone") {
            const digits = String(value || "").replace(/\D/g, "");
            if (digits.length < 9 || digits.length > 11) {
                return "Số điện thoại không hợp lệ (9-11 chữ số)";
            }
        }
    }

    return "";
}

function validateAll(fields, formData) {
    const errors = {};

    fields.forEach((f) => {
        const err = validateField(f, formData[f.key]);
        if (err) errors[f.key] = err;
    });

    return errors;
}

function ContractForm({
    activeType,
    formData,
    setFormData,
    company,
    editingId,
    setTab,
    setActiveType,
    saveContract,
    setShowCompanyModal,
    listPersponel = [],
    fetchAllPersonel,
    provinces = [],
}) {
    const fields = CONTRACT_TYPES[activeType]?.fields || [];

    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    useEffect(() => {
        if (fetchAllPersonel && listPersponel.length === 0) {
            fetchAllPersonel();
        }
    }, [fetchAllPersonel, listPersponel.length]);

    // Tự động chuyển đổi idPlace từ ID sang Title nếu tìm thấy trong provinces
    useEffect(() => {
        if (formData?.idPlace && provinces && provinces.length > 0) {
            const resolvedTitle = getPlaceTitle(formData.idPlace, provinces);
            if (resolvedTitle !== formData.idPlace) {
                setFormData((prev) => ({ ...prev, idPlace: resolvedTitle }));
            }
        }
    }, [provinces, formData?.idPlace, setFormData]);

    useEffect(() => {
        if (!editingId && (!formData?.position || !formData?.desc)) {
            setFormData((prev) => ({
                ...prev,
                position: prev?.position || "Nhân viên kinh doanh",
                desc: prev?.desc || "Tìm kiếm, phát triển mạng lưới đại lý khách hàng kiểm tra và xử lý",
            }));
        }
    }, [editingId, formData?.position, formData?.desc, setFormData]);

    const handleSetActiveType = (k) => {
        setActiveType(k);
        setFormData((prev) => {
            const next = { ...prev };
            if (k === "labor") {
                next.position = prev?.position || "Nhân viên kinh doanh";
                next.desc = prev?.desc || "Tìm kiếm, phát triển mạng lưới đại lý khách hàng kiểm tra và xử lý";
            } else {
                delete next.position;
                delete next.desc;
            }
            return next;
        });
        setErrors({});
        setTouched({});
    };

    const handleChange = useCallback(
        (field, rawValue) => {
            let value = rawValue;
            if (field.key === "salary") {
                value = String(rawValue).replace(/\D/g, "");
            }

            setFormData((d) => {
                const newData = { ...d, [field.key]: value };
                return newData;
            });

            if (touched[field.key]) {
                const err = validateField(field, value);
                setErrors((prev) => ({ ...prev, [field.key]: err }));
            }
        },
        [touched, setFormData]
    );

    const handleBlur = useCallback(
        (field, value) => {
            setTouched((prev) => ({ ...prev, [field.key]: true }));
            const err = validateField(field, value);
            setErrors((prev) => ({ ...prev, [field.key]: err }));
        },
        []
    );

    const isEmployeeSigned = !!(formData?.employeeSignature || formData?.employee_signature);
    const isManagerSigned = !!(formData?.managerSignature || formData?.manager_signature);
    const isFullySigned = (isEmployeeSigned && isManagerSigned) || formData?.sign_status === "completed" || formData?.signStatus === "completed";

    const handleSave = () => {
        if (isFullySigned) {
            message.error("Hợp đồng đã hoàn tất ký kết điện tử bởi cả 2 bên và đã có hiệu lực pháp lý, không thể chỉnh sửa!");
            return;
        }

        const allErrors = validateAll(fields, formData);

        const allTouched = {};
        fields.forEach((f) => (allTouched[f.key] = true));
        setTouched(allTouched);
        setErrors(allErrors);

        if (Object.values(allErrors).some(Boolean)) return;

        saveContract();
        
    };

    const inputStyle = (key) => ({
        ...css.input,
        borderColor: errors[key] && touched[key] ? "#ef4444" : "#e2e8f0",
        outline: "none",
        transition: "border-color 0.15s",
    });

    const errorMsg = (key) =>
        errors[key] && touched[key] ? (
            <span style={{ color: "#ef4444", fontSize: 11, marginTop: 3, display: "block" }}>
                ⚠ {errors[key]}
            </span>
        ) : null;

    const hasErrors = Object.values(errors).some(Boolean);

    

    return (
        <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                <div>
                    <ToolBarCustom
                        title={editingId ? "Chỉnh sửa hợp đồng" : "Tạo hợp đồng mới"}
                        status={false}
                    />
                </div>
                <button style={css.btn("ghost")} onClick={() => setTab("list")}>
                    ← Quay lại
                </button>
            </div>

            {isFullySigned && (
                <Alert
                    type="warning"
                    showIcon
                    message="Hợp đồng đã hoàn tất giao kết điện tử và đã có hiệu lực pháp lý"
                    description="Văn bản này đã được cả Người lao động và Giám đốc ký kết, niêm phong toàn vẹn (SHA-256). Theo quy định pháp luật và chính sách hệ thống, không thể chỉnh sửa hợp đồng đã hoàn tất ký kết."
                    style={{ marginBottom: 16 }}
                />
            )}

            {/* Type & Personnel selector */}
            <div style={css.card}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#64748b", marginBottom: 12 }}>
                    CHỌN NHÂN VIÊN & LOẠI HỢP ĐỒNG
                </div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                    <Select
                        style={{
                            minWidth: 260,
                            height: 44,
                            background: "#f1f5f9",
                            padding: "0 8px",
                            borderRadius: 8,
                        }}
                        dropdownStyle={{ borderRadius: 12 }}
                        placeholder="🔍 Tìm chọn nhân viên..."
                        className="contract-select"
                        showSearch
                        optionFilterProp="label"
                        value={formData.personnel_id || undefined}
                        filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                        onChange={(value) => {
                            const item = listPersponel.find((p) => p.ID === value);
                            if (!item) return;

                            const posName = item?.positions?.title || item?.job_title || "";
                            const addressStr = item?.home_address || item?.current_address || "";
                            const phoneStr = item?.phone || item?.mobile || "";

                            const rawPlace =
                                item?.private_code_place_name ||
                                item?.private_code_place_title ||
                                item?.provinces?.title ||
                                item?.province?.title ||
                                item?.private_code_place_rel?.title ||
                                item?.private_code_place ||
                                "";

                            const placeTitle = getPlaceTitle(rawPlace, provinces);

                            // Lấy ngày tạo tài khoản của sale
                            const rawAccountDate =
                                item?.date_created ||
                                item?.created_at ||
                                item?.user?.date_created ||
                                item?.user?.date_active ||
                                item?.job_date_join ||
                                item?.user?.created_at;

                            let accountDateFormatted = "";
                            let accountDateYMD = "";
                            if (rawAccountDate) {
                                const d = dayjs(rawAccountDate);
                                if (d.isValid()) {
                                    accountDateFormatted = d.format("DD/MM/YYYY");
                                    accountDateYMD = d.format("YYYY-MM-DD");
                                }
                            }

                            setFormData((prev) => ({
                                ...prev,
                                personnel_id: value,
                                employeeName: item.name || "",
                                dob: item.birthday ? item.birthday.split("T")[0] : prev.dob || "",
                                idNumber: item.private_code || prev.idNumber || "",
                                idDate: item.private_code_date ? item.private_code_date.split("T")[0] : prev.idDate || "",
                                idPlace: placeTitle || prev.idPlace || "",
                                address: addressStr || prev.address || "",
                                phone: phoneStr || prev.phone || "",
                                ...(activeType === "labor" ? {
                                    position: prev.position || "Nhân viên kinh doanh",
                                    desc: prev.desc || "Tìm kiếm, phát triển mạng lưới đại lý khách hàng kiểm tra và xử lý",
                                } : {}),
                                startDate: accountDateYMD || prev.startDate || "",
                                taxCode: item.job_tax || prev.taxCode || "",
                                bankAccount: item.job_bank_account || prev.bankAccount || "",
                                bankName: item.bank_name || prev.bankName || "",
                                salary: item.salary_real ? String(item.salary_real) : prev.salary || "",
                            }));
                        }}
                        options={listPersponel.map((item) => ({
                            value: item.ID,
                            label: `${item.name} ${item.private_code ? `(${item.private_code})` : ''}`,
                        }))}
                    />

                    {Object.entries(CONTRACT_TYPES).map(([k, v]) => (
                        <button
                            key={k}
                            style={{ ...css.tabBtn(activeType === k), padding: "10px 20px", fontSize: 14 }}
                            onClick={() => handleSetActiveType(k)}
                            disabled={!!editingId}
                        >
                            {v.icon} {v.label}
                        </button>
                    ))}

                    <button
                        style={{ ...css.btn("ghost"), marginLeft: "auto" }}
                        onClick={() => setShowCompanyModal(true)}
                    >
                        🏢 Thông tin công ty
                    </button>
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                {/* Form */}
                <div style={css.card}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#64748b", marginBottom: 16 }}>
                        THÔNG TIN HỢP ĐỒNG
                    </div>

                    {hasErrors && Object.values(touched).some(Boolean) && (
                        <div
                            style={{
                                background: "#fef2f2",
                                border: "1px solid #fecaca",
                                borderRadius: 8,
                                padding: "10px 14px",
                                marginBottom: 14,
                                fontSize: 13,
                                color: "#b91c1c",
                            }}
                        >
                            ⚠ Vui lòng kiểm tra lại các trường bắt buộc trước khi lưu.
                        </div>
                    )}

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                        {fields.map((f) => (
                            <div
                                key={f.key}
                                style={
                                    f.type === "text" && (f.key === "address" || f.key === "desc")
                                        ? { gridColumn: "1/-1" }
                                        : {}
                                }
                            >
                                <label style={css.label}>
                                    {f.label}
                                    {f.required && <span style={{ color: "#ef4444" }}> *</span>}
                                </label>

                                {f.type === "select" ? (
                                    <>
                                        <select
                                            style={inputStyle(f.key)}
                                            value={formData[f.key] || ""}
                                            onChange={(e) => handleChange(f, e.target.value)}
                                            onBlur={(e) => handleBlur(f, e.target.value)}
                                        >
                                            <option value="">-- Chọn --</option>
                                            {f.options.map((o) => (
                                                <option key={o} value={o}>
                                                    {o}
                                                </option>
                                            ))}
                                        </select>
                                        {errorMsg(f.key)}
                                    </>
                                ) : f.type === "checkbox" ? (
                                    <>
                                        <label
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 8,
                                                cursor: "pointer",
                                                marginTop: 6,
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={!!formData[f.key]}
                                                onChange={(e) => {
                                                    const value = e.target.checked ? 1 : 0;
                                                    setFormData((d) => ({ ...d, [f.key]: value }));
                                                    setTouched((prev) => ({ ...prev, [f.key]: true }));

                                                    const err = validateField(f, value);
                                                    setErrors((prev) => ({ ...prev, [f.key]: err }));
                                                }}
                                                style={{ width: 16, height: 16 }}
                                            />
                                            <span style={{ fontSize: 13 }}>Có</span>
                                        </label>
                                        {errorMsg(f.key)}
                                    </>
                                ) : f.type === "date" ? (
                                    <>
                                        <DatePicker
                                            style={{
                                                width: "100%",
                                                height: 38,
                                                borderRadius: 6,
                                                borderColor: errors[f.key] && touched[f.key] ? "#ef4444" : "#e2e8f0",
                                            }}
                                            format="DD/MM/YYYY"
                                            placeholder="DD/MM/YYYY"
                                            value={formData[f.key] ? dayjs(formData[f.key]) : null}
                                            disabledDate={(current) => {
                                                if (!current) return false;
                                                if (f.key === "startDate" || f.key === "dateStart") {
                                                    return false;
                                                }
                                                if (f.key === "endDate" || f.key === "dateFinish") {
                                                    const startVal = formData.startDate || formData.dateStart;
                                                    if (startVal) {
                                                        return current < dayjs(startVal).startOf("day");
                                                    }
                                                }
                                                return false;
                                            }}
                                            onChange={(date) => {
                                                const val = date ? dayjs(date).format("YYYY-MM-DD") : "";
                                                handleChange(f, val);
                                                setTouched((prev) => ({ ...prev, [f.key]: true }));
                                                const err = validateField(f, val);
                                                setErrors((prev) => ({ ...prev, [f.key]: err }));
                                            }}
                                        />
                                        {errorMsg(f.key)}
                                    </>
                                ) : (
                                    <>
                                        <input
                                            type={f.type}
                                            style={inputStyle(f.key)}
                                            value={
                                                f.key === "salary"
                                                    ? (formData[f.key] !== undefined && formData[f.key] !== null && formData[f.key] !== ""
                                                        ? Number(formData[f.key]).toLocaleString("en-US")
                                                        : "")
                                                    : formData[f.key] || ""
                                            }
                                            onChange={(e) => handleChange(f, e.target.value)}
                                            onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
                                            onBlur={(e) => {
                                                e.target.style.borderColor =
                                                    errors[f.key] && touched[f.key] ? "#ef4444" : "#e2e8f0";
                                                handleBlur(f, e.target.value);
                                            }}
                                        />
                                        {errorMsg(f.key)}
                                    </>
                                )}
                            </div>
                        ))}
                    </div>

                    <div style={{ marginTop: 20, display: "flex", gap: 10, alignItems: "center" }}>
                        {isFullySigned ? (
                            <button
                                style={{
                                    ...css.btn("ghost"),
                                    cursor: "not-allowed",
                                    opacity: 0.65,
                                    border: "1px solid #cbd5e1",
                                    color: "#64748b",
                                    fontWeight: 700,
                                }}
                                disabled
                                title="Hợp đồng đã ký kết đầy đủ 2 bên và có hiệu lực pháp lý, không thể chỉnh sửa!"
                            >
                                🔒 Đã có hiệu lực pháp lý (Không thể sửa)
                            </button>
                        ) : (
                            <button style={css.btn("primary")} onClick={handleSave}>
                                💾 Lưu hợp đồng
                            </button>
                        )}
                    </div>
                </div>

                {/* Live Preview */}
                <div style={css.card}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#64748b", marginBottom: 12 }}>
                        XEM TRƯỚC HỢP ĐỒNG (LIVE PREVIEW)
                    </div>
                    <div
                        style={{
                            border: "1px solid #e2e8f0",
                            borderRadius: 8,
                            overflow: "auto",
                            maxHeight: 900,
                            background: "#fff",
                            transform: "scale(0.8)",
                            transformOrigin: "top left",
                            width: "125%",
                        }}
                    >
                        {activeType === "labor" ? (
                            <LaborContractPreview data={formData} company={company} />
                        ) : (
                            <CollaboratorContractPreview data={formData} company={company} />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default React.memo(ContractForm);